import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { emitAgentSetup } from '../../../api/createAppSocket';
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as Jacob } from "../../../assets/images/ChatImages/albert.svg";
import { ReactComponent as Laptop } from "../../../assets/images/ChatImages/laptop.svg";
import { ListAllRoomsApi } from "../../../store/features/ListAllRooms";
import { getMessageById } from "../../../store/features/getRoomById";
import { markRead } from "../../../store/features/markRead";
import Loader from "../../../assets/Loader";
import SearchInput from "../../common/SearchInputField";
import { ReactComponent as trustchatLogo } from "../../../assets/images/trustchatlogo.svg";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import NoChatAvailable from "./NoChatAvailable";
import ChatListSkeleton from "./ChatListSkeleton";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { ReactComponent as Desktop } from "../../../assets/images/ChatImages/laptop.svg";
import { ReactComponent as Mobile } from "../../../assets/images/mobile-devices.svg";
import { useSocket } from "../../../SocketContext";
import { visitorAppearsOnline } from "../../../utils/index";
import { getLiveChatBasePath } from "../../../utils/liveChatPath";
import { resolveSocketRoomId } from "../../../utils/socketRoomId";
import { socketPayloadIsUnassignedChat } from "../../../utils/unassignedSocketRoom";
import ChatListRowV2 from "../ChatV2/ChatListRowV2";
import { getPendingRoomPreview } from "../../../utils/pendingRoomPreview";
import { ReactComponent as DoubleGreentick } from "../../../assets/images/ChatImages/doubleTick.svg";
import { ReactComponent as DoubleGreytick } from "../../../assets/images/ChatImages/doubleGreyTick.svg";
import { getChatListReadReceiptFromRoom } from "../../../utils/chatListReadReceipt";

function ChatUser(props) {
  const [searchParams] = useSearchParams();
  const [activeChat, setActiveChat] = useState(null);
  const [chatMessage, setChatMessage] = useState("");
  const [messageCounts, setMessageCounts] = useState({});
  const [count, setCount] = useState(0);
  const [rmId, setRmId] = useState(null);
  const [roomUsers, setRoomUsers] = useState([]);

  const [searchOnlyRows, setSearchOnlyRows] = useState(null);

  const {
    soundOn,
    data,
    setData,
    getRoomList,
    selectUser,
    setSelectUser,
    setGetRoomId,
    getRoomId,
    refreshChatList,
    openRoom,
    setAssignedAgentImage,
    typingRooms,
    setTypingIndicator,
    roomClicked,
    setChatSideBar,
    fromSearch = false,
    urlRoomId = "",
    hasMore = false,
    isFetchingMore = false,
    loadMore,
    onRoomAdded,
    variant,
    emptyMessage = "No chats available yet",
  } = props;
  const isV2 = variant === "v2";

  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.loginToken);
  const agentToken = useSelector((state) => state.loginTokenCoUser);
  const userData = useSelector((state) => state.loginToken);
  const agentData = useSelector((state) => state.loginTokenCoUser);
  const [activation, setActivation] = useState();
  const [searchTerm, setSearchTerm] = useState("");

  const socket = useSocket();
  const rmIdRef = useRef(rmId);
  const navigate = useNavigate();
  const listRef = useRef(null);
  const lastActiveTimeRef = useRef(Date.now());
  const pendingUpdatesRef = useRef([]);
  const getRoomListRef = useRef(getRoomList);
  getRoomListRef.current = getRoomList;
  const selectUserRef = useRef(selectUser);
  selectUserRef.current = selectUser;

  const socketHandlersRef = useRef({
    handleEndRooms: () => {},
    handleMarkRead: () => {},
    handleRoomCreated: () => {},
    handleCustomerStatus: () => {},
  });

  const [processedMessageIds, setProcessedMessageIds] = useState(new Set());

  /** No-op: old implementation remounted the whole list (scroll jump + infinite re-renders). */
  const forceUIUpdate = useCallback(() => {}, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;

      console.log('Tab visibility changed:', isVisible ? 'visible' : 'hidden');

      if (isVisible) {
        const timeSinceLastActive = Date.now() - lastActiveTimeRef.current;
        console.log(`Tab visible again after ${timeSinceLastActive}ms`);

        if (pendingUpdatesRef.current.length > 0) {
          console.log('Applying pending updates:', pendingUpdatesRef.current.length);

          pendingUpdatesRef.current.forEach(updateFn => {
            updateFn();
          });

          pendingUpdatesRef.current = [];
        }

        setTimeout(() => {
          forceUIUpdate();
        }, 100);

        if (!socket?.connected) {
          console.log('Socket was disconnected — reconnecting & refreshing rooms');
          socket.connect();

          setTimeout(() => {
            getRoomListRef.current?.(1, { background: true });
            forceUIUpdate();
          }, 500);
        } else {
          // Socket still connected — skip list refetch; it resets isLive/isOnline from stale API/LS.
          setTimeout(() => forceUIUpdate(), 100);
        }
      } else {
        lastActiveTimeRef.current = Date.now();
      }
    };

    const handleWindowFocus = () => {
      console.log('Window focused');

      setTimeout(() => {
        if (!socket?.connected) {
          console.log('Focus: socket was disconnected, refreshing rooms');
          socket.connect();
          setTimeout(() => {
            getRoomListRef.current?.(1, { background: true });
            forceUIUpdate();
          }, 500);
        } else {
          setTimeout(() => forceUIUpdate(), 100);
        }
      }, 100);
    };

    const handleWindowBlur = () => {
      console.log('Window blurred');
      lastActiveTimeRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('pageshow', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('pageshow', handleWindowFocus);
    };
  }, [socket, forceUIUpdate]);

  const updateDataWithForce = useCallback((updateFn) => {
    // Use Page Visibility (document.hidden), not window blur. Blur fires when DevTools
    // or another app is focused while the tab is still visible — queuing there blocked
    // socket-driven list updates until focus returned.
    if (!document.hidden) {
      updateFn();
      setTimeout(() => forceUIUpdate(), 50);
    } else {
      console.log('Document hidden, queuing update until tab visible again');
      pendingUpdatesRef.current.push(updateFn);
    }
  }, [forceUIUpdate]);

  const chracterPosition = (alphabet) => {
    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
    return positionOfAlphabet;
  };

  const colorCodesForAvatar = [
    "#405261", "#FF4D4F", "#FA541C", "#FA8C16", "#FAAD14", "#EB2F96",
    "#FADB14", "#A0D911", "#52C41A", "#5CDBD3", "#9254DE", "#F759AB",
    "#2F54EB", "#722ED1", "#1890FF", "#13C2C2", "#2DB7F5", "#19BE6B",
    "#30A745", "#FA8C16", "#FAAD14", "#9254DE", "#F759AB", "#13C2C2",
    "#722ED1", "#2DB7F5",
  ];

  const appliedUrlRoomRef = useRef(null);

  useEffect(() => {
    const roomIdFromUrl = searchParams.get("roomId");
    if (!roomIdFromUrl) return;

    if (!fromSearch) {
      if (!data?.length) return;
      const roomToSelect = data.find((room) => room.id === roomIdFromUrl);
      if (!roomToSelect) return;
      if (appliedUrlRoomRef.current === roomIdFromUrl) return;
      appliedUrlRoomRef.current = roomIdFromUrl;
      setTypingIndicator(false);
      handleUserClick(roomToSelect);
      RoomByIdHandler(roomToSelect.id);
      setChatSideBar(true);
      if (roomToSelect.num > 0) {
        handleResetNotification(roomToSelect.id, roomToSelect.roomUsers);
      }
      return;
    }

    if (appliedUrlRoomRef.current === roomIdFromUrl) return;
    appliedUrlRoomRef.current = roomIdFromUrl;
    setTypingIndicator(false);
    setChatSideBar(true);
    const roomToSelect = data.find((room) => room.id === roomIdFromUrl);
    if (roomToSelect) {
      handleUserClick(roomToSelect);
      RoomByIdHandler(roomToSelect.id);
      if (roomToSelect.num > 0) {
        handleResetNotification(roomToSelect.id, roomToSelect.roomUsers);
      }
    } else {
      setSelectUser({
        id: roomIdFromUrl,
        title: "Loading...",
        time: "",
        is_archived: false,
        is_unAssigned: false,
        customerId: null,
        roomUsers: [],
        businessId: userData?.response?.user?.business_id,
      });
      handleChatClick(roomIdFromUrl);
      roomClicked(roomIdFromUrl);
      RoomByIdHandler(roomIdFromUrl);
    }
  }, [searchParams, data, fromSearch]);

  // Chat V2: when landing with no selection (and no deep-link roomId), open the top chat.
  useEffect(() => {
    if (!isV2 || fromSearch) return;
    // Only while All is the active tab — avoid racing other tabs during switch.
    const tab = (searchParams.get("tab") || "").toLowerCase();
    if (tab === "unassigned" || tab === "closed" || tab === "archive") return;
    if (searchParams.get("roomId")) return;
    if (selectUserRef.current) return;
    if (!data?.length) return;

    const top = data[0];
    if (!top?.id) return;

    const base = getLiveChatBasePath(window.location.pathname);
    navigate(`${base}?roomId=${top.id}`, { replace: true });
  }, [isV2, fromSearch, searchParams, data, navigate]);

  useEffect(() => {
    if (!fromSearch || !urlRoomId) {
      setSearchOnlyRows(null);
      return;
    }
    if (data?.some((r) => r.id === urlRoomId)) {
      setSearchOnlyRows(null);
      return;
    }
    if (selectUser?.id !== urlRoomId || !getRoomId?.length) return;
    const latest = getRoomId[getRoomId.length - 1];
    const lastVisitor = [...getRoomId].reverse().find((m) => !m?.isAgent);
    const title =
      (selectUser?.title && selectUser.title !== "Loading..."
        ? selectUser.title
        : null) ||
      lastVisitor?.sender_name ||
      latest?.sender_name ||
      "Visitor";
    setSearchOnlyRows([
      {
        id: urlRoomId,
        name: userData?.response?.user?.full_name,
        title,
        logo: <Jacob />,
        description: latest?.content || "",
        attachment: latest?.file_image,
        time: latest?.createdAt ? msgTime(latest.createdAt) : " ",
        startTime: "",
        text: "Windows",
        deviceType: latest?.deviceType || "desktop",
        os: latest?.os,
        McLogo: <Laptop />,
        is_unAssigned: false,
        is_archived: false,
        end_session: false,
        num: 0,
        unAssignedCount: 0,
        iso: latest?.createdAt,
        isLive: false,
        customerId: selectUser?.customerId,
        roomUsers: selectUser?.roomUsers || [],
        businessId: selectUser?.businessId,
        assignedTo: selectUser?.assignedTo,
        tags: selectUser?.tags || [],
        is_guest: selectUser?.is_guest || false,
      },
    ]);
  }, [
    fromSearch,
    urlRoomId,
    data,
    selectUser?.id,
    selectUser?.title,
    selectUser?.customerId,
    selectUser?.roomUsers,
    selectUser?.businessId,
    selectUser?.assignedTo,
    selectUser?.tags,
    selectUser?.is_guest,
    getRoomId,
  ]);

  const showNotification = useCallback(
    (message) => {
      if (
        !document.hasFocus() &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        const notificationOptions = {
          body: message.content || "New message received",
          icon: trustchatLogo,
          badge: trustchatLogo,
          tag: message.roomId,
          data: {
            roomId: message.roomId,
            messageId: message._id,
          },
        };
        const notification = new Notification(
          message.sender_name || "New Chat Message",
          notificationOptions
        );

        notification.onclick = function (event) {
          event.preventDefault();
          window.focus();
          navigate(`${getLiveChatBasePath(window.location.pathname)}?roomId=${this.data.roomId}`);
          notification.close();
        };
      }
    },
    [navigate]
  );

  const handleOpenRoom = () => {
    console.log("ChatUser handleOpenRoom/////////////////////////////////");
    if (!selectUser) {
      return;
    }
    selectUser.is_unAssigned = false;
    let value = selectUser?.id;
    RoomByIdHandler(value);
    handleChatClick(value);
  };

  useEffect(() => {
    handleOpenRoom();
  }, [openRoom]);

  useEffect(() => {
    console.log("Initial <------------------------>");
  }, [refreshChatList]);

  useEffect(() => {
    rmIdRef.current = rmId;
  }, [rmId]);

  const handleRoomCreated = (newRoom) => {
    console.log("Room created:", newRoom);
    const roomId = newRoom?._id;
    if (!roomId) return;
    // This list is fed by roomType=active, which the API defines as assigned
    // rooms only. Adding an unassigned room here made it flash into All (and
    // bump the badge) until the next refetch dropped it; it belongs to the
    // Unassigned tab, which has its own listener for this event.
    if (socketPayloadIsUnassignedChat(newRoom)) return;
    if ((data || []).some((room) => String(room.id) === String(roomId))) return;

    const loggedInAgentId = userData?.response?.user?._id;
    const users = Array.isArray(newRoom.users) ? newRoom.users : [];
    const userObjects = users.filter((u) => typeof u === "object" && u !== null);
    const customerUser =
      userObjects.find(
        (u) => u?._id && u?._id !== loggedInAgentId && u?.is_guest
      ) ||
      userObjects.find((u) => u?._id && u?._id !== loggedInAgentId) ||
      userObjects[0] ||
      null;
    const roomUserIds = users
      .map((u) =>
        typeof u === "object" && u?._id ? u._id : typeof u === "string" ? u : null
      )
      .filter(Boolean);

    // Open the new chat only when nothing is already open (Chat V2).
    const shouldAutoOpen = isV2 && !selectUserRef.current;

    const updateFn = () => {
      setData((prevData) => {
        const roomExists = prevData.some((room) => room.id === roomId);
        if (roomExists) {
          console.log("Room already exists in list, skipping duplicate");
          return prevData;
        }

        // The room payload often has no inline message yet, so fall back to a
        // first message that already arrived over the socket (e.g. the welcome).
        const pendingPreview = getPendingRoomPreview(roomId);
        const latestMessage =
          newRoom?.latestMessageSnapshot || newRoom?.latestMessage || null;

        const transformedRoom = {
          id: roomId,
          title: newRoom.roomName || "Guest",
          description:
            latestMessage?.content ||
            pendingPreview?.description ||
            "",
          time: msgTime(
            latestMessage?.createdAt ||
              pendingPreview?.iso ||
              newRoom.createdAt
          ),
          iso:
            latestMessage?.createdAt ||
            pendingPreview?.iso ||
            newRoom.createdAt,
          num: 0,
          isOnline: false,
          isLive: customerUser ? !!customerUser.socket_active : false,
          end_session: newRoom.end_session || false,
          is_archived: newRoom.is_archived || false,
          is_unAssigned: newRoom.is_unAssigned || false,
          business_id: newRoom.business_id,
          assigned_to: newRoom.assigned_to,
          customerId: customerUser?._id || null,
          roomUsers: roomUserIds.length ? roomUserIds : [],
          deviceType: customerUser?.deviceType || "desktop",
          os: customerUser?.os || "Windows",
          attachment:
            Boolean(latestMessage?.file_image) ||
            Boolean(pendingPreview?.attachment),
          readReceipt: getChatListReadReceiptFromRoom(newRoom, 0),
        };

        return [transformedRoom, ...prevData];
      });
    };

    updateDataWithForce(updateFn);
    onRoomAdded?.(roomId);

    if (shouldAutoOpen) {
      const currentTab = (
        new URLSearchParams(window.location.search).get("tab") || ""
      ).toLowerCase();
      const tabParam = currentTab ? `&tab=${currentTab}` : "";
      const base = getLiveChatBasePath(window.location.pathname);
      navigate(`${base}?roomId=${roomId}${tabParam}`, { replace: true });
    }
  };

  const setRoomOfflineTimeout = (roomId, delay) => {
    setTimeout(() => {
      const updateFn = () => {
        setData((prevData) =>
          prevData.map((room) => {
            if (room.id !== roomId || room.isLive) return room;
            return { ...room, isOnline: false };
          })
        );
      };

      updateDataWithForce(updateFn);

      const roomData = JSON.parse(localStorage.getItem(`room_${roomId}`));
      if (roomData) {
        roomData.isOnline = false;
        localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));
      }
    }, delay);
  };

  const RoomByIdHandler = async (rowId) => {
    if (socket) {
      socket.emit("join chat", rowId);
    }
  };

  const markRoomSessionEnded = (roomId, { archived = false, notice = {} } = {}) => {
    if (!roomId) return;
    const endSessionTime = new Date().toISOString();

    const updateFn = () => {
      setData((prevData) =>
        prevData.map((room) => {
          if (String(room.id) !== String(roomId)) return room;
          return {
            ...room,
            end_session: true,
            ...(archived ? { is_archived: true } : {}),
            ...(notice.endedMessage
              ? { archived_message: notice.endedMessage }
              : {}),
            isOnline: false,
            isLive: false,
          };
        })
      );
    };

    updateDataWithForce(updateFn);

    // Keep the open conversation in sync so the composer disables without refresh.
    setSelectUser((prev) => {
      if (!prev || String(prev.id) !== String(roomId)) return prev;
      return {
        ...prev,
        end_session: true,
        endSession: true,
        endSessionTime: prev.endSessionTime || endSessionTime,
        ...(archived ? { is_archived: true } : {}),
        ...(notice.endedMessage
          ? { archived_message: notice.endedMessage }
          : {}),
        ...(notice.endedByRole ? { endedByRole: notice.endedByRole } : {}),
        ...(notice.endedBy ? { endedBy: notice.endedBy } : {}),
        isOnline: false,
        isLive: false,
      };
    });
  };

  const handleEndRooms = (feedbackData) => {
    markRoomSessionEnded(resolveSocketRoomId(feedbackData), {
      notice:
        feedbackData && typeof feedbackData === "object"
          ? {
              endedMessage:
                feedbackData.endedMessage || feedbackData.archived_message,
              endedByRole: feedbackData.endedByRole,
              endedBy: feedbackData.endedBy,
            }
          : {},
    });
  };

  const handleArchiveChat = (payload) => {
    markRoomSessionEnded(resolveSocketRoomId(payload), { archived: true });
  };

  const handleMarkRead = (roomId) => {
    console.log("Marking room as read:", roomId);
    const updateFn = () => {
      setData((prevData) =>
        prevData.map((room) => {
          if (room.id === roomId) {
            return {
              ...room,
              num: 0,
            };
          }
          return room;
        })
      );
    };

    updateDataWithForce(updateFn);
  };

  /** Visitor opened / read agent messages — same reset-count path as ChatMessageComponent. */
  const handleVisitorResetCount = (roomId) => {
    if (roomId == null) return;
    const updateFn = () => {
      setData((prevData) =>
        prevData.map((room) => {
          if (String(room.id) !== String(roomId)) return room;
          if (room.readReceipt !== "sent") return room;
          return {
            ...room,
            readReceipt: "read",
          };
        })
      );
    };
    updateDataWithForce(updateFn);
  };

  const handleCustomerStatus = useCallback(
    ({ userId, isActive, userType }) => {
      if (userType !== "customer" || userId == null) return;
      const statusUserId = String(userId);
      const nextLive = !!isActive;
      const matchedRoomIds = [];
      const updateFn = () => {
        setData((prevData) =>
          prevData.map((room) => {
            const matches =
              String(room.customerId) === statusUserId ||
              (Array.isArray(room.roomUsers) &&
                room.roomUsers.some((id) => String(id) === statusUserId));
            if (!matches) return room;
            matchedRoomIds.push(String(room.id));
            return {
              ...room,
              isLive: nextLive,
              isOnline: isActive ? room.isOnline : false,
            };
          })
        );
      };
      updateDataWithForce(updateFn);

      // Keep open chat avatar in sync with list presence immediately.
      setSelectUser((prev) => {
        if (!prev || prev.end_session) return prev;
        const matches =
          String(prev.customerId) === statusUserId ||
          (Array.isArray(prev.roomUsers) &&
            prev.roomUsers.some((id) => String(id) === statusUserId)) ||
          matchedRoomIds.includes(String(prev.id));
        if (!matches || !!prev.isLive === nextLive) return prev;
        return {
          ...prev,
          isLive: nextLive,
          isOnline: isActive ? prev.isOnline : false,
        };
      });
    },
    [setData, updateDataWithForce, setSelectUser]
  );

  socketHandlersRef.current = {
    handleEndRooms,
    handleArchiveChat,
    handleMarkRead,
    handleVisitorResetCount,
    handleRoomCreated,
    handleCustomerStatus,
  };

  useEffect(() => {
    if (!socket) return;

    emitAgentSetup(socket);

    const onEndRoom = (...args) => socketHandlersRef.current.handleEndRooms?.(...args);
    const onArchiveChat = (...args) =>
      socketHandlersRef.current.handleArchiveChat?.(...args);
    const onRoomCreated = (...args) =>
      socketHandlersRef.current.handleRoomCreated?.(...args);
    const onMarkRead = (...args) => socketHandlersRef.current.handleMarkRead?.(...args);
    const onResetCount = (...args) =>
      socketHandlersRef.current.handleVisitorResetCount?.(...args);
    const onCustomerStatus = (...args) =>
      socketHandlersRef.current.handleCustomerStatus?.(...args);

    const onConnected = () => {
      console.log("Socket Connected");
    };

    socket.on("connected", onConnected);
    socket.on("end-room", onEndRoom);
    socket.on("archive-chat", onArchiveChat);
    socket.on("room-created-notification", onRoomCreated);
    socket.on("mark-read", onMarkRead);
    socket.on("reset-count", onResetCount);
    socket.on("update_user_status", onCustomerStatus);

    return () => {
      socket.off("connected", onConnected);
      socket.off("end-room", onEndRoom);
      socket.off("archive-chat", onArchiveChat);
      socket.off("room-created-notification", onRoomCreated);
      socket.off("mark-read", onMarkRead);
      socket.off("reset-count", onResetCount);
      socket.off("update_user_status", onCustomerStatus);
    };
  }, [socket, userData?.response?.user?._id]);

  const msgTime = (time) => {
    if (time === undefined || time === null || time === "") return "—";
    const date = new Date(time);
    if (Number.isNaN(date.getTime())) return "—";
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const clock = date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${month} ${day}, ${year} - ${clock}`;
  };

  const notificationSound = new Audio(
    "/sound/notification.mp3"
  );

  const handleReceivedMessage = useCallback(
    (newMessageReceived) => {
      console.log("handle Received Message called", newMessageReceived);

      const checkAndShowNotification = () => {
        if (!newMessageReceived.message.isAgent) {
          showNotification(newMessageReceived.message);
        }
      };

      if (!selectUserRef.current) {
        setTimeout(checkAndShowNotification, 500);
      } else {
        checkAndShowNotification();
      }

      setProcessedMessageIds(
        (prevIds) => new Set([...prevIds, newMessageReceived?.message._id])
      );

      const roomId = newMessageReceived?.message.roomId;
      const newMessageContent = newMessageReceived?.message.content;
      const newFile = newMessageReceived?.message.file_image;
      const isAgent = newMessageReceived?.message.isAgent;
      const latestMessageTime = msgTime(newMessageReceived?.message.createdAt);
      const currentRmId = rmIdRef.current;
      const checkTimestamp = new Date();
      const userRole = userData?.response?.user?.role_name;
      const currentUserId = userData?.response?.user?._id;

      const updateFn = () => {
        setData((prevData) => {
          const roomExists = prevData.some((room) => room.id === roomId);
          console.log("roomExists", prevData);

          const updatedData = prevData.map((room) => {
            let isOnline = room.isOnline;
            let timeoutId = room.timeoutId;

            if (room.id === roomId) {
              room.iso = checkTimestamp.toISOString();
              if (!isAgent) {
                isOnline = true;
                // Visitor is actively messaging — treat as live for presence dots.
                room.isLive = true;
                if (timeoutId) {
                  clearTimeout(room.timeoutId);
                }

                timeoutId = setTimeout(() => {
                  const offlineUpdateFn = () => {
                    setData((prevData) => {
                      return prevData.map((r) => {
                        if (r.id === roomId && r.iso === room.iso && !r.isLive) {
                          return { ...r, isOnline: false };
                        }
                        return r;
                      });
                    });
                  };

                  updateDataWithForce(offlineUpdateFn);

                  const roomData =
                    JSON.parse(localStorage.getItem(`room_${roomId}`)) || {};
                  if (roomData.iso === room.iso) {
                    roomData.isOnline = false;
                    localStorage.setItem(
                      `room_${roomId}`,
                      JSON.stringify(roomData)
                    );
                  }
                }, 90000);
              }

              const roomData = JSON.parse(
                localStorage.getItem(`room_${roomId}`)
              ) || { isOnline: false };

              const shouldIncrementCount =
                !isAgent &&
                (["admin_with_billing", "admin"].includes(userRole) ||
                  currentRmId !== roomId);

              if (shouldIncrementCount && room.id === roomId) {
                console.log("increment called for room:", roomId);
                const currentCount = (parseInt(room.num, 10) || 0) + 1;
                roomData.isOnline = isOnline;
                roomData.iso = room.iso;
                localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));

                return {
                  ...room,
                  time: latestMessageTime,
                  num: currentCount,
                  description: newFile ? "Sent Attachment" : newMessageContent,
                  isOnline: isOnline,
                  isLive: !isAgent ? true : room.isLive,
                  timeoutId,
                };
              } else {
                roomData.isOnline = isOnline;
                roomData.iso = room.iso;
                localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));

                return {
                  ...room,
                  time: latestMessageTime,
                  description: newFile ? "Sent Attachment" : newMessageContent,
                  isOnline: isOnline,
                  isLive: !isAgent ? true : room.isLive,
                  timeoutId,
                };
              }
            }
            return room;
          });

          const roomIndex = updatedData.findIndex((room) => room.id === roomId);
          if (roomIndex !== -1) {
            const [updatedRoom] = updatedData.splice(roomIndex, 1);
            updatedData.unshift(updatedRoom);
          }

          return updatedData;
        });
      };

      updateDataWithForce(updateFn);

      // Keep thread presence in sync when visitor messages bump list isLive.
      if (!isAgent) {
        setSelectUser((prev) => {
          if (!prev || String(prev.id) !== String(roomId) || prev.end_session) {
            return prev;
          }
          if (prev.isLive) return prev;
          return { ...prev, isLive: true, isOnline: true };
        });
      }
    },
    [showNotification, updateDataWithForce, userData?.response?.user, setSelectUser]
  );

  const handleResetNotification = async (roomId, roomUsers) => {
    const userRole = userData?.response?.user?.role_name;
    const userId = userData?.response?.user?._id;

    const isAgentOrAdmin = ["agent", "admin", "admin_with_billing"].includes(
      userRole
    );
    const isInRoom = roomUsers?.includes(userId);

    if (!isAgentOrAdmin || !isInRoom) return;

    try {
      const response = await dispatch(
        markRead({
          requestHeaders: {
            roomId: roomId,
            userId: userId,
          },
        })
      );

      if (response?.payload?.success) {
        socket?.emit("mark-read", roomId);
        const updateFn = () => {
          setData((prevData) =>
            prevData.map((room) =>
              room.id === roomId ? { ...room, num: 0 } : room
            )
          );
        };
        updateDataWithForce(updateFn);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  // A room can gain an unread count while it is already open. A brand new chat
  // is inserted with a count of zero, so opening it never triggers a mark-read,
  // and the background refetch then reports the server's count — which still
  // includes the auto welcome. The agent is looking at the thread, so clear it
  // instead of leaving a badge that only a reload can shift.
  useEffect(() => {
    const openRoomId = selectUser?.id;
    if (!openRoomId) return;

    const openRow = (data || []).find(
      (room) => String(room?.id) === String(openRoomId)
    );
    if (!openRow || !(Number(openRow.num) > 0)) return;

    handleResetNotification(openRow.id, openRow.roomUsers);
  }, [selectUser?.id, data]);

  const handleChatClick = (id) => {
    setActiveChat(id);
    setRmId(id);
  };

  const handleUserClick = (user) => {
    console.log("handleUserClick called with user:", user);
    // Keep archive/closed state from the list row (search can return closed rooms).
    const updatedUser = {
      ...user,
      is_archived: !!user?.is_archived,
    };

    setSelectUser(updatedUser);
    console.log("Updated selectUser:", updatedUser, selectUser);
    handleChatClick(user.id);
    roomClicked(user.id);
  };

  const roomIdForOpen = urlRoomId || searchParams.get("roomId") || "";
  const roomInProps = data?.some((r) => r.id === roomIdForOpen);
  const listSource = useMemo(() => {
    if (fromSearch && searchOnlyRows?.length && !roomInProps) {
      return searchOnlyRows;
    }
    return data;
  }, [fromSearch, searchOnlyRows, roomInProps, data]);

  useEffect(() => {
    if (!selectUser?.id) return;
    const latest = listSource.find(
      (room) => String(room.id) === String(selectUser.id)
    );
    if (!latest) return;

    const nextIsOnline = !!latest.isOnline;
    const nextIsLive = !!latest.isLive;
    const nextEndSession = !!latest.end_session;
    const nextIsArchived = !!latest.is_archived;

    if (
      !!selectUser.isOnline === nextIsOnline &&
      !!selectUser.isLive === nextIsLive &&
      !!selectUser.end_session === nextEndSession &&
      !!selectUser.is_archived === nextIsArchived
    ) {
      return;
    }

    setSelectUser((prev) =>
      prev && String(prev.id) === String(selectUser.id)
        ? {
            ...prev,
            isOnline: nextIsOnline,
            isLive: nextIsLive,
            end_session: nextEndSession,
            endSession: nextEndSession || !!prev.endSession,
            is_archived: nextIsArchived,
            ...(nextEndSession && !prev.endSessionTime
              ? { endSessionTime: new Date().toISOString() }
              : {}),
          }
        : prev
    );
  }, [
    listSource,
    selectUser?.id,
    selectUser?.isOnline,
    selectUser?.isLive,
    selectUser?.end_session,
    selectUser?.is_archived,
    setSelectUser,
  ]);

  const filteredData = listSource.filter((item) =>
    item?.title?.toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  const getItemSize = useCallback(() => (isV2 ? 104 : 100), [isV2]);

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }) => {
      if (!hasMore || isFetchingMore || !loadMore) return;
      const n = filteredData.length;
      if (n === 0) return;
      if (visibleStopIndex >= n - 1) {
        loadMore();
      }
    },
    [hasMore, isFetchingMore, loadMore, filteredData.length]
  );

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.resetAfterIndex(0, true);
  }, [filteredData.length, searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const searchSingleOpen =
    searchParams.get("fromSearch") === "1" ||
    searchParams.get("fromSearch") === "true";

  if (!listSource || listSource.length === 0) {
    if (searchSingleOpen && searchParams.get("roomId")) {
      return <ChatListSkeleton />;
    }
    return <NoChatAvailable message={emptyMessage} />;
  }

  const ChatItem = ({ index, style }) => {
    const item = filteredData[index];
    const isActive = activeChat === item.id;
    const isEndSession = item.end_session;
    const showTyping = typingRooms && typingRooms[item.id];

    const activateRow = () => {
      const currentTab = (new URLSearchParams(window.location.search).get("tab") || "").toLowerCase();
      const tabParam = currentTab ? `&tab=${currentTab}` : "";
      const base = getLiveChatBasePath(window.location.pathname);
      navigate(`${base}?roomId=${item.id}${tabParam}`);
      setChatSideBar(true);
      setTypingIndicator(false);
      handleUserClick(item);
      RoomByIdHandler(item.id);
      setRoomUsers(item.roomUsers);
      if (item.num > 0) {
        handleResetNotification(item.id, item.roomUsers);
      }
    };

    if (isV2) {
      return (
        <ChatListRowV2
          style={style}
          item={item}
          isActive={isActive}
          showTyping={showTyping}
          onClick={activateRow}
        />
      );
    }

    return (
      <div
        style={{
          ...style,
          height: "auto",
          transform: "none",
          willChange: "transform",
          position: "absolute",
          top: style.top,
          left: style.left,
          right: style.right,
          marginTop: "10px",
        }}
      >
        <div
          className={`ChatUser-Container 
          ${isActive ? "ChatUser-ContainerActive" : ""}
          ${isEndSession ? "end-session-active" : ""}
          `}
          key={item.id}
          onClick={activateRow}
        >
          <p>
            {item?.title ? (
              <Avatar
                sx={{
                  bgcolor:
                    colorCodesForAvatar[
                    chracterPosition(item?.title?.charAt(0).toUpperCase())
                    ],
                }}
              >
                {item?.title?.charAt(0).toUpperCase()}
              </Avatar>
            ) : item?.img ? (
              <Avatar
                src={item.img}
                alt="users logo"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                }}
              />
            ) : null}
          </p>
          <div className="Chat-User-Main">
            <div className="Chat-User00">
              <div className="Chat-User-Kathryn">
                {item.status}
                {item?.title ? (
                  <span
                    className={`User-Status-Icon ${
                      visitorAppearsOnline(item) ? "online" : "offline"
                    }`}
                  >
                    {visitorAppearsOnline(item) ? (
                      <p className="onlineStatus"></p>
                    ) : (
                      <p className="offlineStatus"></p>
                    )}
                  </span>
                ) : null}
                <p
                  style={{ textTransform: "capitalize" }}
                  className={`ChatUser-text ${item.end_session ? "end-session-active-title" : ""
                    }`}
                >
                  {item.title}
                </p>
              </div>
              <p className="ChatUser-time">
                {typeof item.time === "string"
                  ? item.time.toLowerCase()
                  : String(item.time ?? "—").toLowerCase()}
              </p>
            </div>
            <div className="Chat-User02">
              <p
                style={{
                  color: item.num !== 0 && "#061522",
                  fontWeight: item.num !== 0 && "500",
                }}
                className={"ChatUser-discreption"}
              >
                {showTyping ? (
                  <span className="Typing">Typing...</span>
                ) : item.attachment ? (
                  "Sent Attachment"
                ) : (
                  item.description || ""
                )}
              </p>

              <p className="ChatUser-time">
                {!showTyping &&
                item.num === 0 &&
                item.readReceipt === "read" ? (
                  <DoubleGreentick className="tick-icon" />
                ) : !showTyping &&
                  item.num === 0 &&
                  item.readReceipt === "sent" ? (
                  <DoubleGreytick className="tick-icon" />
                ) : (
                  item.tickLogo
                )}
              </p>
              <p className="ChatUser-red"> {item.num !== 0 ? item.num : ""}</p>
            </div>

            <div className="Chat-User03">
              {item.deviceType === "desktop" ? <Desktop /> : <Mobile />}
              <p className="ActiveUser-Max">{item?.os}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!isV2 && (
        <div className="ChatBar-SearchInput">
          <SearchInput
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            title=""
          />
        </div>
      )}
      <div
        className="ChatUser-Main-Container ChatUser-Main-Container--virtualized"
        style={{
          height: isV2 ? "100%" : "calc(100vh - 15em)",
          overflow: "hidden",
          padding: 0,
        }}
      >
        <div style={{ height: "100%", width: "100%", minHeight: 0 }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                ref={listRef}
                height={height || 500}
                width={width || 300}
                itemCount={filteredData.length}
                itemSize={getItemSize}
                estimatedItemSize={100}
                overscanCount={2}
                onItemsRendered={handleItemsRendered}
                className="virtualized-chat-list"
                key={`chat-user-rooms-${searchTerm || ""}`}
              >
                {ChatItem}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>
    </>
  );
}

export default ChatUser;